import { hierarchy, tree } from "d3-hierarchy"

const NODE_WIDTH = 160
const NODE_HEIGHT = 220
const SPOUSE_GAP = 140
const UNION_GAP_MULTIPLIER = 2.5 // extra gap between children of different unions

export function buildHierarchy(data) {
  if (!data.people.length) return null

  const peopleMap = new Map(data.people.map(p => [p.nodeKey, p]))
  const childrenMap = buildChildrenMap(data)
  const spouseMap = buildSpouseMap(data, peopleMap)
  const unionChildrenMap = buildUnionChildrenMap(data)

  const rootKey = data.rootNodeKey
  if (!peopleMap.get(rootKey)) return null

  const visited = new Set()
  const root = buildNode(rootKey, peopleMap, childrenMap, spouseMap, visited, unionChildrenMap)

  const h = layoutTree(root)

  // Orphan subtrees
  const orphanKeys = [...peopleMap.keys()].filter(k => !visited.has(k))
  const orphanTrees = []
  for (const k of orphanKeys) {
    if (visited.has(k)) continue
    const sub = buildNode(k, peopleMap, childrenMap, spouseMap, visited, unionChildrenMap)
    if (sub) orphanTrees.push(sub)
  }

  layoutOrphans(h, orphanTrees)
  h._orphanCount = orphanKeys.length
  return h
}

function layoutTree(root) {
  const layout = tree()
    .nodeSize([NODE_WIDTH, NODE_HEIGHT])
    .separation((a, b) => {
      if (a.parent !== b.parent) return 1
      const aKey = a.data._unionKey
      const bKey = b.data._unionKey
      // Different union groups get extra separation
      if (aKey && bKey && aKey !== bKey) return UNION_GAP_MULTIPLIER
      // Node without union next to node with union
      if ((aKey && !bKey) || (!aKey && bKey)) return UNION_GAP_MULTIPLIER
      return 1
    })

  const h = hierarchy(root, d => d.children)
  layout(h)
  positionSpouses(h)
  fixSpouseOverlaps(h)
  return h
}

// After positioning spouses, ensure no visual element overlaps at any level
function fixSpouseOverlaps(root) {
  for (let pass = 0; pass < 5; pass++) {
    let shifted = false
    const levels = new Map()
    root.each(n => {
      const list = levels.get(n.depth) || []
      list.push(n)
      levels.set(n.depth, list)
    })

    for (const [, nodes] of levels) {
      nodes.sort((a, b) => a.x - b.x)

      for (let i = 1; i < nodes.length; i++) {
        const prev = nodes[i - 1]
        const curr = nodes[i]
        const prevRight = rightEdge(prev)
        const currLeft = curr.x - NODE_WIDTH / 2
        const minGap = 10

        if (currLeft < prevRight + minGap) {
          const dx = prevRight + minGap - currLeft
          shiftSubtree(curr, dx)
          shifted = true
        }
      }
    }

    if (shifted) positionSpouses(root)
    else break
  }
}

function layoutOrphans(h, orphanTrees) {
  if (!orphanTrees.length) { h._orphanSubtrees = []; return }

  let maxY = 0
  h.each(n => { if (n.y > maxY) maxY = n.y })
  const baseY = maxY + 280
  let offsetX = 0

  h._orphanSubtrees = []
  for (const sub of orphanTrees) {
    const sh = layoutTree(sub)

    let minX = Infinity, maxX = -Infinity
    sh.each(n => {
      if (n.x < minX) minX = n.x
      const right = rightEdge(n)
      if (right > maxX) maxX = right
    })

    const shiftX = offsetX - minX
    sh.each(n => {
      n.x += shiftX; n.y += baseY
      for (const s of (n.data.spouses || [])) { s._x += shiftX; s._y += baseY }
    })

    offsetX += (maxX - minX) + NODE_WIDTH + 40
    h._orphanSubtrees.push(sh)
  }

  // Center orphans
  const totalW = offsetX - 40
  const shift = -totalW / 2
  for (const sh of h._orphanSubtrees) {
    sh.each(n => {
      n.x += shift
      for (const s of (n.data.spouses || [])) { s._x += shift }
    })
  }
}

function rightEdge(node) {
  const spouses = node.data.spouses || []
  if (spouses.length) return spouses[spouses.length - 1]._x + NODE_WIDTH / 2
  return node.x + NODE_WIDTH / 2
}

// --- Data building ---

function buildChildrenMap(data) {
  const map = new Map()
  for (const p of data.parentages) {
    const children = map.get(p.parentKey) || []
    if (!children.includes(p.childKey)) children.push(p.childKey)
    map.set(p.parentKey, children)
  }
  return map
}

function buildSpouseMap(data, peopleMap) {
  const map = new Map()
  for (const u of data.unions) {
    addSpouse(map, u.person1Key, u.person2Key, u.kind, u.unionKey, peopleMap)
    addSpouse(map, u.person2Key, u.person1Key, u.kind, u.unionKey, peopleMap)
  }
  return map
}

function addSpouse(map, key, spouseKey, kind, unionKey, peopleMap) {
  const spouses = map.get(key) || []
  const spouseData = peopleMap.get(spouseKey)
  if (spouseData) spouses.push({ ...spouseData, unionKind: kind, unionKey })
  map.set(key, spouses)
}

function buildUnionChildrenMap(data) {
  const map = new Map()
  const seen = new Set()
  for (const p of data.parentages) {
    if (!p.unionKey || seen.has(p.childKey)) continue
    seen.add(p.childKey)
    const children = map.get(p.unionKey) || []
    children.push(p.childKey)
    map.set(p.unionKey, children)
  }
  return map
}

// --- Node building ---

function buildNode(key, people, children, spouses, visited, uChildMap) {
  if (visited.has(key)) return null
  visited.add(key)

  const person = people.get(key)
  if (!person) return null

  const nodeSpouses = (spouses.get(key) || []).filter(s => !visited.has(s.nodeKey))
  for (const s of nodeSpouses) {
    visited.add(s.nodeKey)
    if (s.unionKey && uChildMap) {
      s._unionChildKeys = uChildMap.get(s.unionKey) || []
    }
  }

  // Order children by union group, annotate each with _unionKey
  const allChildKeys = children.get(key) || []
  const ordered = orderChildrenByUnion(allChildKeys, nodeSpouses)

  // Build union key lookup for annotation
  const childUnionMap = new Map()
  for (const s of nodeSpouses) {
    for (const ck of (s._unionChildKeys || [])) {
      childUnionMap.set(ck, s.unionKey)
    }
  }

  const childNodes = ordered
    .map(ck => {
      const child = buildNode(ck, people, children, spouses, visited, uChildMap)
      if (child) child._unionKey = childUnionMap.get(ck) || null
      return child
    })
    .filter(Boolean)

  return {
    nodeKey: key,
    data: person,
    spouses: nodeSpouses,
    children: childNodes.length ? childNodes : undefined
  }
}

function orderChildrenByUnion(childKeys, spouses) {
  const claimed = new Set()
  const ordered = []

  for (const spouse of spouses) {
    const unionKids = (spouse._unionChildKeys || [])
      .filter(k => childKeys.includes(k) && !claimed.has(k))
    for (const k of unionKids) claimed.add(k)
    ordered.push(...unionKids)
  }

  for (const k of childKeys) {
    if (!claimed.has(k)) ordered.push(k)
  }
  return ordered
}

// --- Positioning helpers ---

function positionSpouses(root) {
  root.each(node => {
    if (!node.data.spouses?.length) return
    node.data.spouses.forEach((spouse, i) => {
      spouse._x = node.x + (i + 1) * SPOUSE_GAP
      spouse._y = node.y
    })
  })
}

function shiftSubtree(node, dx) {
  node.x += dx
  if (node.data.spouses) {
    for (const s of node.data.spouses) s._x += dx
  }
  if (node.children) {
    for (const c of node.children) shiftSubtree(c, dx)
  }
}

// --- Public API ---

// Toggle collapse for a specific union under a parent node.
// When unionKey is null, falls back to legacy node-wide collapse (all children).
export function toggleCollapse(nodeKey, root, unionKey = null) {
  let found = null
  root.each(node => {
    if (node.data.nodeKey === nodeKey) found = node
  })
  if (!found) return root

  if (unionKey) {
    toggleUnionCollapse(found.data, unionKey)
  } else {
    toggleAllChildrenCollapse(found.data)
  }

  return layoutTree(found.ancestors().pop().data)
}

function toggleUnionCollapse(nodeData, unionKey) {
  nodeData._collapsedByUnion ||= new Map()
  if (nodeData._collapsedByUnion.has(unionKey)) {
    const restored = nodeData._collapsedByUnion.get(unionKey)
    nodeData._collapsedByUnion.delete(unionKey)
    const existing = nodeData.children || []
    nodeData.children = mergeChildrenByUnionOrder([...existing, ...restored], nodeData.spouses)
  } else {
    const all = nodeData.children || []
    const toCollapse = all.filter(c => c._unionKey === unionKey)
    const remaining = all.filter(c => c._unionKey !== unionKey)
    if (toCollapse.length) {
      nodeData._collapsedByUnion.set(unionKey, toCollapse)
      nodeData.children = remaining.length ? remaining : undefined
    }
  }
}

function toggleAllChildrenCollapse(nodeData) {
  if (nodeData.children) {
    nodeData._children = nodeData.children
    nodeData.children = undefined
  } else if (nodeData._children) {
    nodeData.children = nodeData._children
    nodeData._children = undefined
  }
}

function mergeChildrenByUnionOrder(children, spouses) {
  if (!spouses?.length) return children
  const unionOrder = spouses.map(s => s.unionKey).filter(Boolean)
  return children.sort((a, b) => {
    const ai = unionOrder.indexOf(a._unionKey)
    const bi = unionOrder.indexOf(b._unionKey)
    if (ai === -1 && bi === -1) return 0
    if (ai === -1) return 1
    if (bi === -1) return -1
    return ai - bi
  })
}

export { NODE_WIDTH, NODE_HEIGHT, SPOUSE_GAP }
